<template>
  <PublicLayout page-title="Cómo funciona">
    <div class="how-it-works-page">
    <!-- ===================== -->
    <!-- HERO SECTION          -->
    <!-- ===================== -->
    <section class="bg-organic relative py-24 overflow-hidden">
      <!-- Background decorations -->
      <div class="absolute top-0 right-0 w-96 h-96 bg-accent-200/20 rounded-full blur-3xl" />
      <div class="absolute bottom-0 left-0 w-80 h-80 bg-teal-200/20 rounded-full blur-3xl" />

      <div class="relative max-w-4xl mx-auto px-6 text-center z-10">
        <!-- Badge -->
        <div class="inline-flex items-center gap-2 px-4 py-2 bg-accent-50 border border-accent-200
                    rounded-full text-accent-700 text-sm font-medium mb-6 animate-fade-up">
          <ClockIcon class="w-4 h-4" />
          <span>Proceso transparente y plazos reales</span>
        </div>

        <!-- Titulo -->
        <h1 class="text-5xl md:text-6xl lg:text-7xl font-bold text-text-primary
                   tracking-tight mb-6 animate-fade-up delay-100">
          Cómo funciona
          <span class="bg-gradient-to-r from-accent-600 to-teal-500 bg-clip-text text-transparent">
            Olvidadme
          </span>
        </h1>

        <!-- Subtitulo -->
        <p class="text-xl md:text-2xl text-text-secondary max-w-2xl mx-auto mb-8
                  animate-fade-up delay-200">
          Sin promesas vacías. Te explicamos exactamente qué esperar en cada etapa del proceso.
        </p>

        <!-- Time estimate -->
        <div class="inline-flex items-center gap-3 bg-white
                    rounded-full px-6 py-3 shadow-lg border border-slate-200 animate-fade-up delay-300">
          <CalendarDaysIcon class="w-5 h-5 text-accent-500" />
          <span class="text-sm text-text-secondary">Tiempo total estimado:</span>
          <span class="font-bold text-text-primary">30-45 días</span>
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- PROCESS TIMELINE      -->
    <!-- ===================== -->
    <section class="py-24 bg-white">
      <div class="max-w-5xl mx-auto px-6">
        <!-- Section Header -->
        <div class="text-center mb-20 animate-fade-up">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            4 pasos hacia tu privacidad digital
          </h2>
          <p class="text-xl text-text-secondary max-w-2xl mx-auto">
            Cada etapa está diseñada para maximizar el éxito de tu solicitud
          </p>
        </div>

        <!-- Timeline -->
        <div class="timeline-connector space-y-8 animate-fade-up delay-100">
          <ProcessStep
            v-for="(step, index) in steps"
            :key="step.number"
            :number="step.number"
            :title="step.title"
            :duration="step.duration"
            :description="step.description"
            :note="step.note"
            :isLast="index === steps.length - 1"
          />
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- WHY IT WORKS          -->
    <!-- ===================== -->
    <section class="py-24 bg-slate-50/50">
      <div class="max-w-6xl mx-auto px-6">
        <div class="text-center mb-16 animate-fade-up">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Por qué funciona nuestro proceso
          </h2>
          <p class="text-text-secondary max-w-2xl mx-auto">
            Combinamos experiencia legal con tecnología para conseguir los mejores resultados
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8 animate-fade-up delay-100">
          <!-- Card 1 -->
          <div class="bg-white rounded-2xl p-8 border border-slate-200
                      hover:shadow-lg transition-shadow">
            <div class="w-14 h-14 bg-accent-100 rounded-2xl flex items-center justify-center mb-6">
              <ScaleIcon class="w-7 h-7 text-accent-600" />
            </div>
            <h3 class="text-xl font-bold text-text-primary mb-3">Base legal sólida</h3>
            <p class="text-text-secondary">
              El artículo 17 del RGPD forma parte de la normativa europea de protección de datos.
              Los responsables del tratamiento están obligados a responder a las solicitudes de
              ejercicio de derechos en el plazo legalmente establecido, pudiendo enfrentarse a
              sanciones administrativas en caso de incumplimiento.
            </p>
          </div>

          <!-- Card 2 -->
          <div class="bg-white rounded-2xl p-8 border border-slate-200
                      hover:shadow-lg transition-shadow">
            <div class="w-14 h-14 bg-accent-100 rounded-2xl flex items-center justify-center mb-6">
              <DocumentTextIcon class="w-7 h-7 text-accent-600" />
            </div>
            <h3 class="text-xl font-bold text-text-primary mb-3">Documentos profesionales</h3>
            <p class="text-text-secondary">
              Nuestros abogados especializados generan solicitudes que cumplen todos los
              requisitos formales, aumentando drásticamente la tasa de aceptación.
            </p>
          </div>

          <!-- Card 3 -->
          <div class="bg-white rounded-2xl p-8 border border-slate-200
                      hover:shadow-lg transition-shadow">
            <div class="w-14 h-14 bg-accent-100 rounded-2xl flex items-center justify-center mb-6">
              <ShieldCheckIcon class="w-7 h-7 text-accent-600" />
            </div>
            <h3 class="text-xl font-bold text-text-primary mb-3">Escalado ante la AEPD</h3>
            <p class="text-text-secondary">
              En caso de denegación injustificada y previa valoración jurídica, el servicio
              incluye la presentación de reclamación ante la AEPD si se considera procedente.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- GUARANTEES SECTION    -->
    <!-- ===================== -->
    <section class="py-24 bg-white">
      <div class="max-w-6xl mx-auto px-6">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8 animate-fade-up">
          <div class="text-center">
            <div class="w-14 h-14 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
              <ShieldCheckIcon class="w-7 h-7 text-accent-600" />
            </div>
            <div class="font-bold text-text-primary mb-1">GDPR Compliant</div>
            <div class="text-sm text-text-secondary">Cumplimiento legal garantizado</div>
          </div>
          <div class="text-center">
            <div class="w-14 h-14 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
              <LockClosedIcon class="w-7 h-7 text-accent-600" />
            </div>
            <div class="font-bold text-text-primary mb-1">Datos encriptados</div>
            <div class="text-sm text-text-secondary">Protección AES-256</div>
          </div>
          <div class="text-center">
            <div class="w-14 h-14 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
              <CalendarDaysIcon class="w-7 h-7 text-accent-600" />
            </div>
            <div class="font-bold text-text-primary mb-1">30-45 días</div>
            <div class="text-sm text-text-secondary">Plazo estimado proceso</div>
          </div>
          <div class="text-center">
            <div class="w-14 h-14 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
              <CurrencyEuroIcon class="w-7 h-7 text-accent-600" />
            </div>
            <div class="font-bold text-text-primary mb-1">Reembolso 100%</div>
            <div class="text-sm text-text-secondary">Si no cumple requisitos</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- FAQ SECTION           -->
    <!-- ===================== -->
    <section class="py-24 bg-slate-50/50">
      <div class="max-w-3xl mx-auto px-6">
        <div class="text-center mb-12 animate-fade-up">
          <span class="text-sm font-semibold text-accent-600 uppercase tracking-widest mb-4 block">FAQ</span>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Preguntas sobre el proceso
          </h2>
        </div>

        <div class="space-y-4 animate-fade-up delay-100">
          <FAQItem
            v-for="(faq, index) in faqs"
            :key="index"
            :question="faq.question"
            :answer="faq.answer"
            :open="faq.open"
          />
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- CTA SECTION           -->
    <!-- ===================== -->
    <section class="py-24 bg-gradient-to-br from-accent-600 to-teal-600 relative overflow-hidden">
      <!-- Decoracion -->
      <div class="absolute inset-0 opacity-10">
        <div class="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
        <div class="absolute bottom-0 right-1/4 w-80 h-80 bg-white rounded-full blur-3xl" />
      </div>

      <div class="relative max-w-3xl mx-auto px-6 text-center">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 animate-fade-up">
          Empieza a proteger tu privacidad hoy
        </h2>
        <p class="text-xl text-white/80 mb-8 animate-fade-up delay-100">
          El proceso es simple. Nosotros nos encargamos de todo lo demás.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up delay-200">
          <RouterLink
            to="/pricing"
            class="px-8 py-4 bg-white text-accent-600 font-bold rounded-2xl
                   hover:bg-white/90 transition-all shadow-lg
                   hover:shadow-xl hover:scale-105"
          >
            Ver planes y precios
          </RouterLink>
          <RouterLink
            to="/services"
            class="px-8 py-4 bg-white/10 text-white font-semibold rounded-2xl
                   border border-white/30 hover:bg-white/20 transition-all"
          >
            Explorar servicios
          </RouterLink>
        </div>
      </div>
    </section>
    </div>
  </PublicLayout>
</template>

<script setup>
import { onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useSeo } from '@/composables/useSeo'
import {
  ClockIcon,
  CalendarDaysIcon,
  ScaleIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  CurrencyEuroIcon
} from '@heroicons/vue/24/outline'
import ProcessStep from '@/components/ui/ProcessStep.vue'
import FAQItem from '@/components/ui/FAQItem.vue'
import PublicLayout from '@/components/layout/PublicLayout.vue'

// SEO Meta Tags
useSeo({
  title: 'Cómo Funciona',
  description: '4 pasos para ejercer tu derecho al olvido. Proceso transparente y plazos reales.',
  canonical: '/como-funciona'
})

/**
 * HowItWorks Page - V3 Storytelling Design
 *
 * Dedicated page explaining the Olvidadme process step by step.
 * Based on LandingProcess.vue but expanded as a full page.
 */

// Process steps
const steps = [
  {
    number: 1,
    title: 'Registro y pago',
    duration: '5 minutos',
    description: 'Creas tu cuenta, eliges tu plan y proporcionas la información básica sobre tu caso. El proceso es 100% online y seguro.',
    note: null
  },
  {
    number: 2,
    title: 'Análisis de viabilidad',
    duration: '72h hábiles',
    description: 'Nuestro equipo legal analiza tu caso y valora su viabilidad jurídica conforme al RGPD art. 17, evaluando de forma individualizada cada plataforma y publicación. El análisis determina si existen fundamentos legales suficientes para solicitar la supresión, sin que ello implique garantía de aceptación por parte de terceros responsables del tratamiento.',
    note: {
      type: 'success',
      text: 'Si se determina que no existen fundamentos legales suficientes, reembolso 100%'
    }
  },
  {
    number: 3,
    title: 'Preparación y envío',
    duration: '48-72h',
    description: 'Generamos los documentos legales personalizados con todos los requisitos formales y enviamos las solicitudes a cada plataforma.',
    note: null
  },
  {
    number: 4,
    title: 'Seguimiento y resolución',
    duration: '30-45 días',
    description: 'Monitorizamos todas las respuestas y el estado de tu solicitud. Conforme al RGPD, artículo 12, las organizaciones deben responder en el plazo general de un mes, con posibilidad de ampliación en casos complejos. Te mantenemos informado en cada paso del proceso.',
    note: {
      type: 'info',
      text: 'En caso de denegación injustificada y previa valoración jurídica, el servicio puede incluir la presentación de reclamación ante la AEPD, si se considera procedente.'
    }
  }
]

// FAQ data
const faqs = [
  {
    question: '¿Qué pasa si una empresa no responde en 1 mes?',
    answer: 'Si una empresa no responde dentro del plazo legal de 1 mes establecido por el GDPR, nosotros iniciamos el proceso de reclamación formal ante la Agencia Española de Protección de Datos (AEPD). Este servicio está incluido en todos nuestros servicios de Derecho al Olvido.',
    open: true
  },
  {
    question: '¿Puedo hacer seguimiento del estado de mis solicitudes?',
    answer: 'Sí, tendrás acceso a un panel de control donde podrás ver el estado de cada solicitud en tiempo real: enviada, en proceso, aceptada o rechazada. También recibirás notificaciones por email con cada actualización importante.',
    open: false
  },
  {
    question: '¿Qué información necesito proporcionar?',
    answer: 'Para iniciar el proceso necesitamos: tu nombre completo, DNI/NIE (encriptado), email de contacto, y las URLs o nombres de las plataformas donde quieres ejercer tu derecho al olvido. No necesitamos acceso a tus cuentas.',
    open: false
  },
  {
    question: '¿Cuánto tiempo tarda el proceso completo?',
    answer: 'El proceso completo suele tardar entre 30 y 45 días. Esto incluye el análisis de viabilidad (72h), preparación de documentos (48-72h), y el plazo legal de respuesta de las empresas (1 mes). En casos complejos puede extenderse si hay reclamaciones ante la AEPD.',
    open: false
  }
]

</script>

<style scoped>
@reference "@/assets/styles/main.css";

.how-it-works-page {
  min-height: 100vh;
  background-color: #f9fafb;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .how-it-works-page *,
  .how-it-works-page *::before,
  .how-it-works-page *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
